import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { ApiResponse } from '../models/api-response';
import {
  BulkEnrollmentResultDto,
  ChildCourseProgressDto,
  ContinueLearningItemDto,
  CourseProgressReportDto,
  EnrollmentCandidateDto,
  EnrollmentDto,
  EnrollmentSearchDto,
  EnrollmentSettingsDto,
  LessonProgressResultDto,
  SelfEnrollResultDto,
  UpdateEnrollmentSettingsDto,
  VideoProgressDto,
  CourseCategoryDto,
  CourseDetailDto,
  CourseFormLookupsDto,
  CourseListItemDto,
  CourseOutlineDto,
  CourseSearchDto,
  LessonAttachmentDto,
  LessonDetailDto,
  LessonType,
  LinkedItemDto,
  LmsFileDto,
  LmsFilePurpose,
  ReorderLessonsDto,
  UpsertCourseCategoryDto,
  UpsertCourseDto,
  UpsertCourseModuleDto,
  UpsertLessonDto,
  CertificateTemplateDto,
  CertificateVerificationDto,
  CourseAnnouncementDto,
  CourseCertificateSettingsDto,
  CourseRatingSummaryDto,
  CourseReviewDto,
  CourseReviewSearchDto,
  CreateDiscussionThreadDto,
  DiscussionPostDto,
  DiscussionThreadDetailDto,
  DiscussionThreadListItemDto,
  DiscussionThreadSearchDto,
  IssueCertificatesResultDto,
  IssuedCertificateDto,
  ModerateThreadDto,
  UpsertCertificateTemplateDto,
  UpsertCourseAnnouncementDto,
  CatalogCourseDetailDto,
  CatalogCourseItemDto,
  CatalogFacetsDto,
  CatalogPathDetailDto,
  CatalogSearchDto,
  LearningPathCourseDto,
  LearningPathCourseItemDto,
  LearningPathCourseOptionDto,
  LearningPathDetailDto,
  LearningPathEnrollResultDto,
  LearningPathEnrollmentDto,
  LearningPathEnrollmentSearchDto,
  LearningPathFormLookupsDto,
  LearningPathListItemDto,
  LearningPathSearchDto,
  UpsertLearningPathDto,
} from '../models/course';

/** LMS API client. Auth + X-Tenant-ID headers are added by the JwtInterceptor. */
@Injectable({ providedIn: 'root' })
export class CourseService {
  private readonly api = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // ── Categories ──────────────────────────────────────────────────────────
  listCategories(): Observable<ApiResponse<CourseCategoryDto[]>> {
    return this.http.get<ApiResponse<CourseCategoryDto[]>>(`${this.api}/CourseCategories/List`);
  }
  createCategory(dto: UpsertCourseCategoryDto): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.api}/CourseCategories/Create`, dto);
  }
  updateCategory(dto: UpsertCourseCategoryDto): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.api}/CourseCategories/Update`, dto);
  }
  deleteCategory(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/CourseCategories/Delete/${id}`);
  }

  // ── Courses (authoring) ─────────────────────────────────────────────────
  list(search: CourseSearchDto): Observable<ApiResponse<CourseListItemDto[]>> {
    return this.http.post<ApiResponse<CourseListItemDto[]>>(`${this.api}/Courses/List`, search);
  }
  getForEdit(id: string): Observable<ApiResponse<CourseDetailDto>> {
    return this.http.get<ApiResponse<CourseDetailDto>>(`${this.api}/Courses/GetForEdit/${id}`);
  }
  create(dto: UpsertCourseDto): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.api}/Courses/Create`, dto);
  }
  update(dto: UpsertCourseDto): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.api}/Courses/Update`, dto);
  }
  delete(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/Courses/Delete/${id}`);
  }
  publish(id: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/Courses/Publish/${id}`, {});
  }
  unpublish(id: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/Courses/Unpublish/${id}`, {});
  }
  archive(id: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/Courses/Archive/${id}`, {});
  }
  duplicate(id: string): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.api}/Courses/Duplicate/${id}`, {});
  }
  formLookups(): Observable<ApiResponse<CourseFormLookupsDto>> {
    return this.http.get<ApiResponse<CourseFormLookupsDto>>(`${this.api}/Courses/FormLookups`);
  }
  linkableItems(type: LessonType, search: string): Observable<ApiResponse<LinkedItemDto[]>> {
    return this.http.get<ApiResponse<LinkedItemDto[]>>(`${this.api}/Courses/LinkableItems`, {
      params: { type: String(type), search: search ?? '' },
    });
  }

  // ── Modules & lessons ───────────────────────────────────────────────────
  createModule(dto: UpsertCourseModuleDto): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.api}/CourseContent/CreateModule`, dto);
  }
  updateModule(dto: UpsertCourseModuleDto): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.api}/CourseContent/UpdateModule`, dto);
  }
  deleteModule(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/CourseContent/DeleteModule/${id}`);
  }
  reorderModules(courseId: string, moduleIds: string[]): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/CourseContent/ReorderModules`, { courseId, moduleIds });
  }
  getLesson(id: string): Observable<ApiResponse<LessonDetailDto>> {
    return this.http.get<ApiResponse<LessonDetailDto>>(`${this.api}/CourseContent/GetLesson/${id}`);
  }
  createLesson(dto: UpsertLessonDto): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.api}/CourseContent/CreateLesson`, dto);
  }
  updateLesson(dto: UpsertLessonDto): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.api}/CourseContent/UpdateLesson`, dto);
  }
  deleteLesson(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/CourseContent/DeleteLesson/${id}`);
  }
  reorderLessons(dto: ReorderLessonsDto): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/CourseContent/ReorderLessons`, dto);
  }
  moveLesson(lessonId: string, targetModuleId: string, targetIndex: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/CourseContent/MoveLesson`, {
      lessonId,
      targetModuleId,
      targetIndex,
    });
  }
  addAttachment(lessonId: string, fileId: string, displayName?: string): Observable<ApiResponse<LessonAttachmentDto>> {
    return this.http.post<ApiResponse<LessonAttachmentDto>>(`${this.api}/CourseContent/AddAttachment`, {
      lessonId,
      fileId,
      displayName,
    });
  }
  removeAttachment(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/CourseContent/RemoveAttachment/${id}`);
  }

  // ── Player ──────────────────────────────────────────────────────────────
  myCourses(search: CourseSearchDto): Observable<ApiResponse<CourseListItemDto[]>> {
    return this.http.post<ApiResponse<CourseListItemDto[]>>(`${this.api}/CoursePlayer/MyCourses`, search);
  }
  outline(id: string): Observable<ApiResponse<CourseOutlineDto>> {
    return this.http.get<ApiResponse<CourseOutlineDto>>(`${this.api}/CoursePlayer/Outline/${id}`);
  }
  playerLesson(id: string): Observable<ApiResponse<LessonDetailDto>> {
    return this.http.get<ApiResponse<LessonDetailDto>>(`${this.api}/CoursePlayer/Lesson/${id}`);
  }

  // ── Enrollment (staff) ──────────────────────────────────────────────────
  enrollmentSettings(courseId: string): Observable<ApiResponse<EnrollmentSettingsDto>> {
    return this.http.get<ApiResponse<EnrollmentSettingsDto>>(`${this.api}/CourseEnrollments/Settings/${courseId}`);
  }
  updateEnrollmentSettings(dto: UpdateEnrollmentSettingsDto): Observable<ApiResponse<EnrollmentSettingsDto>> {
    return this.http.put<ApiResponse<EnrollmentSettingsDto>>(`${this.api}/CourseEnrollments/UpdateSettings`, dto);
  }
  regenerateJoinCode(courseId: string): Observable<ApiResponse<EnrollmentSettingsDto>> {
    return this.http.post<ApiResponse<EnrollmentSettingsDto>>(`${this.api}/CourseEnrollments/RegenerateCode/${courseId}`, {});
  }
  enrollments(search: EnrollmentSearchDto): Observable<ApiResponse<EnrollmentDto[]>> {
    return this.http.post<ApiResponse<EnrollmentDto[]>>(`${this.api}/CourseEnrollments/List`, search);
  }
  enrollmentCandidates(courseId: string, search: string, gradeId: string | null): Observable<ApiResponse<EnrollmentCandidateDto[]>> {
    const params: Record<string, string> = { search: search ?? '' };
    if (gradeId) params['gradeId'] = gradeId;
    return this.http.get<ApiResponse<EnrollmentCandidateDto[]>>(`${this.api}/CourseEnrollments/Candidates/${courseId}`, { params });
  }
  enrollStudents(courseId: string, studentIds: string[]): Observable<ApiResponse<BulkEnrollmentResultDto>> {
    return this.http.post<ApiResponse<BulkEnrollmentResultDto>>(`${this.api}/CourseEnrollments/Enroll`, { courseId, studentIds });
  }
  unenrollStudents(courseId: string, studentIds: string[]): Observable<ApiResponse<BulkEnrollmentResultDto>> {
    return this.http.post<ApiResponse<BulkEnrollmentResultDto>>(`${this.api}/CourseEnrollments/Unenroll`, { courseId, studentIds });
  }
  reviewEnrollments(courseId: string, enrollmentIds: string[], approve: boolean): Observable<ApiResponse<BulkEnrollmentResultDto>> {
    return this.http.post<ApiResponse<BulkEnrollmentResultDto>>(`${this.api}/CourseEnrollments/Review`, { courseId, enrollmentIds, approve });
  }
  syncEnrollments(courseId: string): Observable<ApiResponse<BulkEnrollmentResultDto>> {
    return this.http.post<ApiResponse<BulkEnrollmentResultDto>>(`${this.api}/CourseEnrollments/Sync/${courseId}`, {});
  }
  progressReport(courseId: string): Observable<ApiResponse<CourseProgressReportDto>> {
    return this.http.get<ApiResponse<CourseProgressReportDto>>(`${this.api}/CourseEnrollments/Progress/${courseId}`);
  }
  exportProgressCsv(courseId: string): Observable<Blob> {
    return this.http.get(`${this.api}/CourseEnrollments/ExportProgress/${courseId}`, { responseType: 'blob' });
  }

  // ── Learning (students) ─────────────────────────────────────────────────
  selfEnroll(courseId: string): Observable<ApiResponse<SelfEnrollResultDto>> {
    return this.http.post<ApiResponse<SelfEnrollResultDto>>(`${this.api}/CourseLearning/SelfEnroll/${courseId}`, {});
  }
  joinByCode(code: string): Observable<ApiResponse<SelfEnrollResultDto>> {
    return this.http.post<ApiResponse<SelfEnrollResultDto>>(`${this.api}/CourseLearning/JoinByCode`, { code });
  }
  markComplete(lessonId: string): Observable<ApiResponse<LessonProgressResultDto>> {
    return this.http.post<ApiResponse<LessonProgressResultDto>>(`${this.api}/CourseLearning/MarkComplete/${lessonId}`, {});
  }
  reportVideoProgress(dto: VideoProgressDto): Observable<ApiResponse<LessonProgressResultDto>> {
    return this.http.post<ApiResponse<LessonProgressResultDto>>(`${this.api}/CourseLearning/VideoProgress`, dto);
  }
  continueLearning(take = 4): Observable<ApiResponse<ContinueLearningItemDto[]>> {
    return this.http.get<ApiResponse<ContinueLearningItemDto[]>>(`${this.api}/CourseLearning/ContinueLearning`, { params: { take: String(take) } });
  }

  // ── Parents ─────────────────────────────────────────────────────────────
  childCourses(childId: string): Observable<ApiResponse<ChildCourseProgressDto[]>> {
    return this.http.get<ApiResponse<ChildCourseProgressDto[]>>(`${this.api}/ParentCourses/ChildCourses`, { params: { childId } });
  }

  // ── L4: discussions ─────────────────────────────────────────────────────
  discussionThreads(search: DiscussionThreadSearchDto): Observable<ApiResponse<DiscussionThreadListItemDto[]>> {
    return this.http.post<ApiResponse<DiscussionThreadListItemDto[]>>(`${this.api}/CourseDiscussions/Threads`, search);
  }
  discussionThread(id: string): Observable<ApiResponse<DiscussionThreadDetailDto>> {
    return this.http.get<ApiResponse<DiscussionThreadDetailDto>>(`${this.api}/CourseDiscussions/Thread/${id}`);
  }
  createThread(dto: CreateDiscussionThreadDto): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.api}/CourseDiscussions/CreateThread`, dto);
  }
  updateThread(id: string, title: string, body: string): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.api}/CourseDiscussions/UpdateThread`, { id, title, body });
  }
  deleteThread(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/CourseDiscussions/DeleteThread/${id}`);
  }
  replyToThread(threadId: string, body: string): Observable<ApiResponse<DiscussionPostDto>> {
    return this.http.post<ApiResponse<DiscussionPostDto>>(`${this.api}/CourseDiscussions/Reply`, { threadId, body });
  }
  updateReply(id: string, body: string): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.api}/CourseDiscussions/UpdateReply`, { id, body });
  }
  deleteReply(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/CourseDiscussions/DeleteReply/${id}`);
  }
  moderateThread(dto: ModerateThreadDto): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/CourseDiscussions/ModerateThread`, dto);
  }
  moderateReply(postId: string, isHidden: boolean): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/CourseDiscussions/ModerateReply`, { postId, isHidden });
  }
  markAnswer(threadId: string, postId: string | null): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/CourseDiscussions/MarkAnswer`, { threadId, postId });
  }

  // ── L4: announcements ───────────────────────────────────────────────────
  announcements(courseId: string, pageNumber = 1, pageSize = 20): Observable<ApiResponse<CourseAnnouncementDto[]>> {
    return this.http.post<ApiResponse<CourseAnnouncementDto[]>>(`${this.api}/CourseAnnouncements/List`, { courseId, pageNumber, pageSize });
  }
  createAnnouncement(dto: UpsertCourseAnnouncementDto): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.api}/CourseAnnouncements/Create`, dto);
  }
  updateAnnouncement(dto: UpsertCourseAnnouncementDto): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.api}/CourseAnnouncements/Update`, dto);
  }
  deleteAnnouncement(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/CourseAnnouncements/Delete/${id}`);
  }

  // ── L4: reviews ─────────────────────────────────────────────────────────
  reviewSummary(courseId: string): Observable<ApiResponse<CourseRatingSummaryDto>> {
    return this.http.get<ApiResponse<CourseRatingSummaryDto>>(`${this.api}/CourseReviews/Summary/${courseId}`);
  }
  reviews(search: CourseReviewSearchDto): Observable<ApiResponse<CourseReviewDto[]>> {
    return this.http.post<ApiResponse<CourseReviewDto[]>>(`${this.api}/CourseReviews/List`, search);
  }
  saveMyReview(courseId: string, rating: number, comment: string | null): Observable<ApiResponse<CourseReviewDto>> {
    return this.http.post<ApiResponse<CourseReviewDto>>(`${this.api}/CourseReviews/SaveMine`, { courseId, rating, comment });
  }
  deleteMyReview(courseId: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/CourseReviews/DeleteMine/${courseId}`);
  }
  respondToReview(reviewId: string, response: string | null): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/CourseReviews/Respond`, { reviewId, response });
  }
  hideReview(reviewId: string, isHidden: boolean, reason: string | null): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/CourseReviews/Hide`, { reviewId, isHidden, reason });
  }

  // ── L4: certificates ────────────────────────────────────────────────────
  certificateTemplates(): Observable<ApiResponse<CertificateTemplateDto[]>> {
    return this.http.get<ApiResponse<CertificateTemplateDto[]>>(`${this.api}/CertificateTemplates/List`);
  }
  createCertificateTemplate(dto: UpsertCertificateTemplateDto): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.api}/CertificateTemplates/Create`, dto);
  }
  updateCertificateTemplate(dto: UpsertCertificateTemplateDto): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.api}/CertificateTemplates/Update`, dto);
  }
  deleteCertificateTemplate(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/CertificateTemplates/Delete/${id}`);
  }
  /** Sample PDF (blob); validation errors come back as a JSON blob. */
  previewCertificateTemplate(dto: UpsertCertificateTemplateDto): Observable<Blob> {
    return this.http.post(`${this.api}/CertificateTemplates/Preview`, dto, { responseType: 'blob' });
  }
  courseCertificateSettings(courseId: string): Observable<ApiResponse<CourseCertificateSettingsDto>> {
    return this.http.get<ApiResponse<CourseCertificateSettingsDto>>(`${this.api}/Certificates/CourseSettings/${courseId}`);
  }
  updateCourseCertificateSettings(courseId: string, certificateEnabled: boolean, certificateTemplateId: string | null): Observable<ApiResponse<CourseCertificateSettingsDto>> {
    return this.http.put<ApiResponse<CourseCertificateSettingsDto>>(`${this.api}/Certificates/UpdateCourseSettings`, { courseId, certificateEnabled, certificateTemplateId });
  }
  courseCertificates(courseId: string, search: string, status: number | null, pageNumber = 1, pageSize = 50): Observable<ApiResponse<IssuedCertificateDto[]>> {
    return this.http.post<ApiResponse<IssuedCertificateDto[]>>(`${this.api}/Certificates/CourseList`, { courseId, search, status, pageNumber, pageSize });
  }
  issueCertificates(courseId: string, studentIds: string[], allCompleted: boolean): Observable<ApiResponse<IssueCertificatesResultDto>> {
    return this.http.post<ApiResponse<IssueCertificatesResultDto>>(`${this.api}/Certificates/Issue`, { courseId, studentIds, allCompleted });
  }
  revokeCertificate(certificateId: string, reason: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/Certificates/Revoke`, { certificateId, reason });
  }
  myCertificates(): Observable<ApiResponse<IssuedCertificateDto[]>> {
    return this.http.get<ApiResponse<IssuedCertificateDto[]>>(`${this.api}/Certificates/My`);
  }
  childCertificates(childId: string): Observable<ApiResponse<IssuedCertificateDto[]>> {
    return this.http.get<ApiResponse<IssuedCertificateDto[]>>(`${this.api}/Certificates/ChildCertificates`, { params: { childId } });
  }
  downloadCertificate(id: string): Observable<Blob> {
    return this.http.get(`${this.api}/Certificates/Download/${id}`, { responseType: 'blob' });
  }
  /** Anonymous public verification. */
  verifyCertificate(code: string): Observable<ApiResponse<CertificateVerificationDto>> {
    return this.http.get<ApiResponse<CertificateVerificationDto>>(`${this.api}/Certificates/Verify/${encodeURIComponent(code)}`);
  }

  // ── L5: learning paths (staff) ──────────────────────────────────────────
  listPaths(search: LearningPathSearchDto): Observable<ApiResponse<LearningPathListItemDto[]>> {
    return this.http.post<ApiResponse<LearningPathListItemDto[]>>(`${this.api}/LearningPaths/List`, search);
  }
  getPathForEdit(id: string): Observable<ApiResponse<LearningPathDetailDto>> {
    return this.http.get<ApiResponse<LearningPathDetailDto>>(`${this.api}/LearningPaths/GetForEdit/${id}`);
  }
  createPath(dto: UpsertLearningPathDto): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.api}/LearningPaths/Create`, dto);
  }
  updatePath(dto: UpsertLearningPathDto): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.api}/LearningPaths/Update`, dto);
  }
  deletePath(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.api}/LearningPaths/Delete/${id}`);
  }
  publishPath(id: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/LearningPaths/Publish/${id}`, {});
  }
  unpublishPath(id: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/LearningPaths/Unpublish/${id}`, {});
  }
  archivePath(id: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.api}/LearningPaths/Archive/${id}`, {});
  }
  savePathCourses(learningPathId: string, items: LearningPathCourseItemDto[]): Observable<ApiResponse<LearningPathCourseDto[]>> {
    return this.http.put<ApiResponse<LearningPathCourseDto[]>>(`${this.api}/LearningPaths/SaveCourses`, { learningPathId, items });
  }
  pathCourseOptions(search: string): Observable<ApiResponse<LearningPathCourseOptionDto[]>> {
    return this.http.get<ApiResponse<LearningPathCourseOptionDto[]>>(`${this.api}/LearningPaths/CourseOptions`, { params: { search: search ?? '' } });
  }
  pathFormLookups(): Observable<ApiResponse<LearningPathFormLookupsDto>> {
    return this.http.get<ApiResponse<LearningPathFormLookupsDto>>(`${this.api}/LearningPaths/FormLookups`);
  }
  uploadPathCover(learningPathId: string, file: File): Observable<HttpEvent<ApiResponse<LmsFileDto>>> {
    const form = new FormData();
    form.append('learningPathId', learningPathId);
    form.append('file', file, file.name);
    return this.http.post<ApiResponse<LmsFileDto>>(`${this.api}/LearningPaths/UploadCover`, form, {
      reportProgress: true,
      observe: 'events',
    });
  }
  pathStudents(search: LearningPathEnrollmentSearchDto): Observable<ApiResponse<LearningPathEnrollmentDto[]>> {
    return this.http.post<ApiResponse<LearningPathEnrollmentDto[]>>(`${this.api}/LearningPaths/Students`, search);
  }
  pathCandidates(pathId: string, search: string, gradeId: string | null): Observable<ApiResponse<EnrollmentCandidateDto[]>> {
    const params: Record<string, string> = { search: search ?? '' };
    if (gradeId) params['gradeId'] = gradeId;
    return this.http.get<ApiResponse<EnrollmentCandidateDto[]>>(`${this.api}/LearningPaths/Candidates/${pathId}`, { params });
  }
  enrollPathStudents(learningPathId: string, studentIds: string[]): Observable<ApiResponse<BulkEnrollmentResultDto>> {
    return this.http.post<ApiResponse<BulkEnrollmentResultDto>>(`${this.api}/LearningPaths/Enroll`, { learningPathId, studentIds });
  }
  unenrollPathStudents(learningPathId: string, studentIds: string[]): Observable<ApiResponse<BulkEnrollmentResultDto>> {
    return this.http.post<ApiResponse<BulkEnrollmentResultDto>>(`${this.api}/LearningPaths/Unenroll`, { learningPathId, studentIds });
  }
  reviewPathEnrollments(learningPathId: string, enrollmentIds: string[], approve: boolean): Observable<ApiResponse<BulkEnrollmentResultDto>> {
    return this.http.post<ApiResponse<BulkEnrollmentResultDto>>(`${this.api}/LearningPaths/Review`, { learningPathId, enrollmentIds, approve });
  }
  issuePathCertificates(learningPathId: string, studentIds: string[], allCompleted: boolean): Observable<ApiResponse<IssueCertificatesResultDto>> {
    return this.http.post<ApiResponse<IssueCertificatesResultDto>>(`${this.api}/LearningPaths/IssueCertificates`, { learningPathId, studentIds, allCompleted });
  }

  // ── L5: catalog ─────────────────────────────────────────────────────────
  catalogCourses(search: CatalogSearchDto): Observable<ApiResponse<CatalogCourseItemDto[]>> {
    return this.http.post<ApiResponse<CatalogCourseItemDto[]>>(`${this.api}/Catalog/Courses`, search);
  }
  catalogPaths(search: CatalogSearchDto): Observable<ApiResponse<LearningPathListItemDto[]>> {
    return this.http.post<ApiResponse<LearningPathListItemDto[]>>(`${this.api}/Catalog/Paths`, search);
  }
  catalogFacets(): Observable<ApiResponse<CatalogFacetsDto>> {
    return this.http.get<ApiResponse<CatalogFacetsDto>>(`${this.api}/Catalog/Facets`);
  }
  catalogCourse(id: string): Observable<ApiResponse<CatalogCourseDetailDto>> {
    return this.http.get<ApiResponse<CatalogCourseDetailDto>>(`${this.api}/Catalog/Course/${id}`);
  }
  catalogPath(id: string): Observable<ApiResponse<CatalogPathDetailDto>> {
    return this.http.get<ApiResponse<CatalogPathDetailDto>>(`${this.api}/Catalog/Path/${id}`);
  }
  enrollInPath(id: string): Observable<ApiResponse<LearningPathEnrollResultDto>> {
    return this.http.post<ApiResponse<LearningPathEnrollResultDto>>(`${this.api}/Catalog/EnrollPath/${id}`, {});
  }

  // ── Files ───────────────────────────────────────────────────────────────
  upload(courseId: string, purpose: LmsFilePurpose, file: File): Observable<HttpEvent<ApiResponse<LmsFileDto>>> {
    const form = new FormData();
    form.append('courseId', courseId);
    form.append('purpose', purpose);
    form.append('file', file, file.name);
    return this.http.post<ApiResponse<LmsFileDto>>(`${this.api}/LmsFiles/Upload`, form, {
      reportProgress: true,
      observe: 'events',
    });
  }

  /** Authorized blob download (files are never exposed through public URLs). */
  downloadBlob(fileId: string): Observable<Blob> {
    return this.http.get(`${this.api}/LmsFiles/Download/${fileId}`, { responseType: 'blob' });
  }
}
